import React from 'react';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import { Button } from '@this/components/shared/ui/inputs/button';
import Tooltip from '@this/shared/tooltip/tooltip';
import { digits } from '@this/src/util';
import { Link } from '@this/shared/ui/navigations/link';

import type SearchQuery from '../../../../../domain/search_query';
import type SearchQueryItem from '../../../../../domain/search_query_item';
import type SelectStore from '../../../../../domain/select_store';
import SearchBlock from '../../../../shared/search/search_block/search_block';
import OutlineItemShortDistance from '../../outline_item/OutlineItemShortDistance';

interface Props {
  query: SearchQuery;
  store: SelectStore;
  selectErrors: string[];
  submitting: boolean;
  handleSelectItem: () => void;
  handleAddItem?: (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  additionalSearchErrors?: { [k in string]: string };
  handleRemoveItem?: () => void;

  handleNextButtonClick: (e: React.MouseEvent<HTMLElement>) => void;
  handleOpenShowSearchBox: () => void;
  isRequestForm: boolean; // 旧コメント: ArrangementRequestForm
}

const OutlineShortDistanceContainer = ({
  query,
  store,
  selectErrors,
  handleSelectItem,
  handleAddItem,
  handleRemoveItem,
  handleAdditionalSearch,
  additionalSearchErrors,
  handleNextButtonClick,
  submitting,
  isRequestForm,
  handleOpenShowSearchBox
}: Props) => {
  const getDisplayItems = () => {
    if (store.showPackage && store.result.type === 'airPackage') {
      return store.result.packageItems[store.result.airline];
    }

    if (store.showPackage && store.result.type === 'jrPackage') {
      return store.result.jrPackageItems;
    }

    return store.result.items;
  };

  return (
    <StyledOutline isRequestForm={isRequestForm}>
      {getDisplayItems().map((item, i) => (
        <OutlineItemShortDistance
          key={item.index}
          sectionNumber={i + 1}
          item={item}
          handleClickTab={store.handleClickTab}
          handleSelectItem={handleSelectItem}
          selected={store.currentTab === item.index}
          hotelPriceLimit={item.hotelPriceLimit}
          store={store}
          isRequestForm={isRequestForm}
          handleOpenShowSearchBox={handleOpenShowSearchBox}
        />
      ))}

      {store.result.type === 'separate' &&
        handleAddItem &&
        store.result.isMultipleLocationsSearchResult() &&
        (store.result.items.length === store.query.items.length ? (
          <div className="select-outline__item-block">
            <StyledBody>
              {store.result.transportAvailable && (
                <Link
                  type="submit"
                  disabled={query.isItemLimit()}
                  onClick={e => handleAddItem('transport', e)}
                  style={{ marginRight: '10px' }}
                >
                  +交通機関を追加
                </Link>
              )}
              {store.result.hotelAvailable && (
                <Link type="submit" disabled={query.isItemLimit()} onClick={e => handleAddItem('hotel', e)}>
                  +ホテルを追加
                </Link>
              )}
            </StyledBody>
          </div>
        ) : store.result.items.length < store.query.items.length ? (
          <div className="select-outline__item-block">
            <StyledBody>
              <form
                className="search"
                onSubmit={e => handleAdditionalSearch(store.query.items[store.query.items.length - 1], e)}
              >
                <SearchBlock item={store.query.items[store.query.items.length - 1]} />
                <div className="search__button-wrapper">
                  {additionalSearchErrors && (
                    <div className="search__error">
                      {Object.keys(additionalSearchErrors).map((key, i) => (
                        <div className="error" key={i}>
                          {additionalSearchErrors[key]}
                        </div>
                      ))}
                    </div>
                  )}
                  <StyledButtonGroup>
                    <Button type="submit" disabled={submitting}>
                      検索
                    </Button>
                    <Link onClick={handleRemoveItem}>キャンセル</Link>
                  </StyledButtonGroup>
                </div>
              </form>
            </StyledBody>
          </div>
        ) : null)}

      <div className="select-outline__item-block">
        <StyledHeader>
          <StyledIcon>↑</StyledIcon>
          {store.result.type === 'separate' ? (
            <StyledDate>
              合計金額：
              <StyledPrice>
                <span data-wovn-ignore>{digits(store.result.getReservingTrip().totalPrice())}</span>
                <StyledYen>円</StyledYen>
              </StyledPrice>
              <Tooltip type="info">
                <div className="select__tooltip-format">
                  {store.result
                    .getReservingTrip()
                    .tooltipText()
                    .split('\n')
                    .map((l: string, i: number) => (
                      <p key={i}>{l}</p>
                    ))}
                </div>
              </Tooltip>
            </StyledDate>
          ) : (
            <StyledPackagePriceArea>
              <StyledWrap>
                <StyledContent>
                  <StyledPackageTitle>
                    パッケージプラン
                    <Tooltip type="info">
                      <div className="select__tooltip-format">
                        {store.result
                          .getPackageTrip()
                          .tooltipText()
                          .split('\n')
                          .map((l: string, i: number) => (
                            <p key={i}>{l}</p>
                          ))}
                      </div>
                    </Tooltip>
                  </StyledPackageTitle>
                  <StyledPrice>
                    合計：
                    <StyledPriceMain data-wovn-ignore>
                      {digits(store.result.getPackageTrip().totalPrice())}
                    </StyledPriceMain>{' '}
                    円 (税込)
                  </StyledPrice>
                </StyledContent>
              </StyledWrap>
            </StyledPackagePriceArea>
          )}
        </StyledHeader>
      </div>

      {isRequestForm === false && (
        <>
          <StyledReserveButton
            className={`select-pc__reserve-button ${submitting ? 'loading' : ''}`}
            onClick={handleNextButtonClick}
            disabled={submitting}
            loading={submitting}
            color="primary"
            size="medium"
          >
            予約確認画面へ
          </StyledReserveButton>
          {selectErrors.length > 0 && (
            <div className="select__errors-bottom">
              {selectErrors.map((error, i) => (
                <div className="error" key={i}>
                  {error}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </StyledOutline>
  );
};
const StyledHeader = styled.div`
  display: flex;
`;

const StyledIcon = styled.div`
  text-align: center;
  width: 20px;
  height: 20px;
  background: ${props => props.theme.iconColor};
  border-radius: 10px;
  color: white;
  font-weight: bold;
  margin-right: 10px;
`;

const StyledDate = styled.div`
  display: flex;
  font-weight: bold;
  margin-right: auto;
`;

const StyledBody = styled.div`
  margin-left: 8px;
  border-left: solid 4px ${props => props.theme.iconColor};
  padding: 10px 0 10px 10px;
`;

const StyledOutline = styled.div<{ isRequestForm: boolean }>`
  padding: ${props => (props.isRequestForm ? '15px 10px' : '10px 10px 60px')};
`;

const StyledPrice = styled.span`
  color: ${props => props.theme.redColor};
  vertical-align: text-bottom;
  display: inline;
`;

const StyledYen = styled.span`
  font-size: 9px;
  margin-left: 3px;
`;

const StyledPriceMain = styled.span`
  font-size: 18px;
`;

const StyledReserveButton = styled(Button)`
  display: none;
  position: relative;
  width: 214px;
  margin: 40px auto;
  ${media.sp`
    display: block;
  `}
`;

const StyledPackagePriceArea = styled.div`
  display: flex;
  width: 100%;
  height: 77px;
  background: #f6f4ee;
`;

const StyledPackageTitle = styled.div`
  font-size: 11px;
  display: flex;
  margin-bottom: 5px;
  justify-content: center;
  color: #222222;
`;

const StyledContent = styled.div`
  margin: auto;
`;

const StyledWrap = styled.div`
  display: flex;
  font-weight: bold;
  width: 100%;
`;

const StyledButtonGroup = styled.div`
  display: flex;
  align-items: center;

  *:not(:first-child) {
    margin-left: 10px;
  }
`;

export default OutlineShortDistanceContainer;
